import type { DbExecutor } from "./executor";
import { timestampForFilename, type FileStore } from "./fileStore";
import type { AttachmentRepository } from "./repositories/attachmentRepository";

/**
 * Back Up All Data (Phase 5).
 *
 * The SQLite file alone is no longer the complete user dataset once
 * attachments exist. This service:
 *   1. takes a CONSISTENT database snapshot via `VACUUM INTO` (an atomic,
 *      transaction-consistent copy — never a raw copy of a live file that
 *      may be mid-write or carry WAL state);
 *   2. reads every managed attachment file the database references;
 *   3. packages snapshot + attachments + a restore README into one zip;
 *   4. writes it to backups/berrier-reno-backup-YYYYMMDD-HHMM.zip.
 * Any failure (snapshot, missing/unreadable attachment, write) throws —
 * a backup is never reported successful unless everything was captured.
 */
export interface BackupResult {
  relativePath: string;
  absolutePath: string;
  attachmentCount: number;
  sizeBytes: number;
}

export const DATABASE_RELATIVE_PATH = "berrier-reno.sqlite";

export class BackupService {
  constructor(
    private readonly db: DbExecutor,
    private readonly files: FileStore,
    private readonly attachments: AttachmentRepository,
    private readonly now: () => Date = () => new Date(),
  ) {}

  async backupAll(): Promise<BackupResult> {
    // Desktop-only; the zip library loads only when a backup is actually made.
    const { zipSync, strToU8 } = await import("fflate");
    const stamp = timestampForFilename(this.now());
    const snapshotRel = `backups/.snapshot-${stamp}.sqlite`;
    const zipRel = `backups/berrier-reno-backup-${stamp}.zip`;

    // 1. Consistent snapshot. VACUUM INTO refuses to overwrite, so make sure
    //    the target does not exist. Path is an OS absolute path for SQLite.
    await this.files.removeFile(snapshotRel);
    const snapshotAbs = await this.files.absolutePath(snapshotRel);
    // Backups dir must exist for VACUUM INTO to write into it.
    await this.files.writeFile("backups/.keep", new Uint8Array([0]));
    await this.db.execute(`VACUUM INTO '${snapshotAbs.replace(/'/g, "''")}'`);
    if (!(await this.files.exists(snapshotRel))) {
      throw new Error("Database snapshot was not created; backup aborted.");
    }

    try {
      const entries: Record<string, Uint8Array> = {};
      entries[DATABASE_RELATIVE_PATH] = await this.files.readFile(snapshotRel);

      // 2. Attachments the database references (archived included — evidence).
      const attachments = await this.attachments.listAll();
      const missing: string[] = [];
      for (const a of attachments) {
        if (!(await this.files.exists(a.storedRelativePath))) {
          missing.push(`${a.originalFilename} (${a.storedRelativePath})`);
          continue;
        }
        entries[a.storedRelativePath] = await this.files.readFile(a.storedRelativePath);
      }
      if (missing.length > 0) {
        throw new Error(
          `Backup aborted: ${missing.length} attachment file(s) are missing on disk: ${missing.join("; ")}`,
        );
      }

      entries["BACKUP-README.txt"] = strToU8(
        [
          "Berrier Reno full data backup",
          `Created: ${this.now().toISOString()}`,
          "",
          "Contents:",
          `  ${DATABASE_RELATIVE_PATH}   — consistent SQLite snapshot (VACUUM INTO)`,
          "  attachments/...          — managed receipt/invoice files",
          "",
          "Manual restore:",
          "  1. Close Berrier Reno completely.",
          "  2. Open %APPDATA%\\com.berrierholdings.berrierreno\\",
          "  3. Move the existing berrier-reno.sqlite and attachments\\ aside (keep them).",
          "  4. Extract this zip into that folder so berrier-reno.sqlite and",
          "     attachments\\ sit directly inside it.",
          "  5. Start Berrier Reno. Migrations run automatically if needed.",
        ].join("\r\n"),
      );

      // 3. Package (stored, no compression for images/PDFs; DB compresses well).
      const zipped = zipSync(entries, { level: 6 });
      await this.files.writeFile(zipRel, zipped);
      return {
        relativePath: zipRel,
        absolutePath: await this.files.absolutePath(zipRel),
        attachmentCount: attachments.length,
        sizeBytes: zipped.byteLength,
      };
    } finally {
      await this.files.removeFile(snapshotRel);
    }
  }
}
